import React from "react";
import { useAppSettingsService } from "src/services/appSettingsService/appSettingsService";
import { useSelector } from "src/store";
import { makeStyles } from "tss-react/mui";

import { selectGroups } from "@dashboard/groups/store/selectors";
import { useCurrentWorkspaceFeatures } from "@dashboard/workspaces/api/helpers";
import Grid from "@mui/material/Grid";

import { EventChartCard } from "../EventChartCard/EventChartCard.lazy";

import { EnrollFirstDeviceSection } from "./EnrollFirstDeviceSection/EnrollFirstDeviceSection";
import EnrollmentProgressCard from "./EnrollmentProgressCard/EnrollmentProgressCard";
import { ManagedAppsCard } from "./ManagedAppsCard/ManagedAppsCard";
import OnlineStatusCard from "./OnlineStatusCard/OnlineStatusCard";
import { OperatingSystemsCard } from "./OperatingSystemsCard/OperatingSystemsCard";
import { ProductsCard } from "./ProductsCard/ProductsCard";
import SubscriptionStatusCard from "./SubscriptionStatusCard/SubscriptionStatusCard";
import { WarningCard } from "./WarningCard";

// The cells of a row are already equally tall; the card inside the cell has to fill its cell so all four cards line up.
const useStyles = makeStyles()(() => ({
    statusCardCell: {
        display: "flex",
        "& > *": {
            flex: 1,
        },
    },
}));

export const StatisticSection = (props: { filter: number[] }) => {
    const { classes } = useStyles();
    const { effectiveWorkspaceSettings } = useAppSettingsService();
    const activityChartEnabled = effectiveWorkspaceSettings.isOverviewPageShowingActivityChart;
    const groups = useSelector(selectGroups);
    const groupsIds = React.useMemo(() => groups.map((group) => group.id), [groups]);
    const groupsForStatistics = React.useMemo(() => (props.filter.length ? props.filter : groupsIds), [props.filter, groupsIds]);

    const { data: features } = useCurrentWorkspaceFeatures();
    const licenseUsed = features?.find((feature) => feature.feature === "LicenseLimit")?.used;
    const hasConsumedLicense = (licenseUsed ?? 0) > 0;
    const showEnrollFirstDevice = features !== undefined && !hasConsumedLicense;

    return (
        <Grid container spacing={1}>
            <Grid size={{ xs: 12, md: 3 }} className={classes.statusCardCell}>
                <SubscriptionStatusCard />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }} className={classes.statusCardCell}>
                <EnrollmentProgressCard />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }} className={classes.statusCardCell}>
                <OnlineStatusCard groupIds={groupsForStatistics} />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }} className={classes.statusCardCell}>
                <WarningCard groupIds={groupsForStatistics} />
            </Grid>
            {showEnrollFirstDevice ? (
                <Grid size={{ xs: 12, md: 12 }}>
                    <EnrollFirstDeviceSection />
                </Grid>
            ) : null}
            {hasConsumedLicense ? (
                <>
                    {activityChartEnabled && (
                        <Grid size={{ xs: 12, md: 12 }}>
                            <EventChartCard groupIds={groupsForStatistics} />
                        </Grid>
                    )}
                    <Grid size={{ xs: 12, md: 4 }}>
                        <ManagedAppsCard groupIds={groupsForStatistics} />
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                        <ProductsCard groupIds={groupsForStatistics} />
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                        <OperatingSystemsCard groupIds={groupsForStatistics} />
                    </Grid>
                </>
            ) : null}
        </Grid>
    );
};
